import { useLayoutEffect } from 'react';

const DEVICE_WIDTH = 'width=device-width, initial-scale=1';
/*
 * What a phone does with no tag at all: lay the page out 980px wide and shrink it to fit. Written
 * out rather than removing the tag, because Chrome keeps the last width it was given when the tag
 * is removed: leaving the admin page, or closing the sign-in dialog, left the rest of the app at
 * phone width until a reload.
 */
const DESKTOP_WIDTH = 'width=980';

// Every mounted caller, so the admin page and the sign-in dialog open over it don't undo each other.
let requests = 0;
let tag: HTMLMetaElement | null = null;

function apply(): void {
  if (tag) tag.content = requests > 0 ? DEVICE_WIDTH : DESKTOP_WIDTH;
}

/**
 * Lays the page out at the device's own width while the calling component is mounted.
 *
 * index.html has no viewport meta tag, so a phone lays every page out 980px wide and shrinks it to
 * fit: on the admin page that left 13px text at about 5px. Adding the tag to index.html would change
 * every page at once, the editor included, so a component opts in here instead. A page whose HTML
 * already has its own tag is left alone. Desktop browsers ignore the tag.
 *
 * A layout effect, so the first paint is already at the right width.
 */
export function useDeviceWidthViewport(): void {
  useLayoutEffect(() => {
    if (!tag) {
      if (document.querySelector('meta[name="viewport"]')) {
        return undefined;
      }
      tag = document.createElement('meta');
      tag.name = 'viewport';
      document.head.appendChild(tag);
    }
    requests += 1;
    apply();
    return () => {
      requests -= 1;
      apply();
    };
  }, []);
}

export default useDeviceWidthViewport;
